import { createContext, useEffect, useState } from "react";
import { supabase } from "../supabase";

export const AuthContext = createContext(null);

// 🌍 IP 기반 국가 감지
async function detectCountry() {
  try {
    const res = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(3000) });
    if (!res.ok) return null;
    const data = await res.json();
    return data.country_name || null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /* -------------------------
      🔹 세션 + 프로필 로드
  ------------------------- */
  const loadUser = async (session) => {
    if (!session?.user) {
      setUser(null);
      setLoading(false);
      return;
    }

    const authUser = session.user;

    const { data: profile } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", authUser.id)
      .maybeSingle();

    // 🔒 차단된 유저는 강제 로그아웃
    if (profile?.is_blocked) {
      await supabase.auth.signOut();
      setUser(null);
      setLoading(false);
      return;
    }

    // 🔥 provider 판별
    const provider = authUser.app_metadata?.provider || "email";

    // 🔥 profile이 없거나 username이 NULL이면 보정
    if (!profile || !profile.username) {
      const metadataUsername =
        authUser.user_metadata?.username ||
        authUser.user_metadata?.full_name ||
        authUser.user_metadata?.name;

      const username =
        metadataUsername && metadataUsername.trim().length > 0
          ? metadataUsername.trim()
          : `user_${authUser.id.slice(0, 6)}`;

      const country = await detectCountry();

      await supabase.from("profiles").upsert({
        id: authUser.id,
        email: authUser.email,
        username: username,
        role: "user",
        plan: "FREE",
        is_blocked: false,
        provider: provider,
        ...(country && { country }),
      });

      const { data: updatedProfile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", authUser.id)
        .single();

      setUser({
        ...authUser,
        ...(updatedProfile || {}),
      });

    } else {
      // 기존 프로필에 provider 또는 country가 없으면 업데이트
      const updates = {};
      if (!profile.provider && provider !== "email") updates.provider = provider;
      if (!profile.country) {
        const country = await detectCountry();
        if (country) updates.country = country;
      }
      if (Object.keys(updates).length > 0) {
        await supabase.from("profiles").update(updates).eq("id", authUser.id);
        Object.assign(profile, updates);
      }

      setUser({
        ...authUser,
        ...profile,
      });
    }

    setLoading(false);
  };

  /* -------------------------
      🔹 초기 로드
  ------------------------- */
  useEffect(() => {
    // Supabase recovery 토큰 처리 (URL hash에 access_token이 포함됨)
    const hash = window.location.hash;
    const tokenMatch = hash.match(/access_token=([^&]+)/);
    const refreshMatch = hash.match(/refresh_token=([^&]+)/);
    const typeMatch = hash.match(/type=recovery/);

    if (tokenMatch && refreshMatch && typeMatch) {
      supabase.auth.setSession({
        access_token: tokenMatch[1],
        refresh_token: refreshMatch[1],
      }).then(({ data, error }) => {
        if (error) {
          console.error("Recovery session error:", error);
          window.location.href = "/login";
          return;
        }
        loadUser(data.session);
        // 토큰 제거하고 비밀번호 재설정 페이지로 이동
        window.location.href = "/update-password?reset=1";
      });
    } else {
      supabase.auth.getSession().then(({ data }) => {
        loadUser(data.session);
      });
    }

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        loadUser(session);
      }
    );

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  /* -------------------------
      🔹 인증 함수들
  ------------------------- */
  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) return { error };

    const signedInUser = data?.user;
    if (!signedInUser?.id) {
      return { error: { message: "로그인 사용자 정보를 확인할 수 없습니다." } };
    }

    const { data: existingProfile, error: profileSelectError } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", signedInUser.id)
      .maybeSingle();

    if (profileSelectError) {
      return { error: profileSelectError };
    }

    // 🔒 차단된 유저 로그인 거부
    if (existingProfile?.is_blocked) {
      await supabase.auth.signOut();
      return { error: { message: "차단된 계정입니다. 관리자에게 문의하세요." } };
    }

    if (!existingProfile || !existingProfile.username) {
      console.log("LOGIN METADATA:", signedInUser.user_metadata);
      const metadataUsername =
        signedInUser.user_metadata?.username;

      const username =
        metadataUsername && metadataUsername.trim().length > 0
          ? metadataUsername.trim()
          : `user_${signedInUser.id.slice(0, 6)}`;

      const loginProvider = signedInUser.app_metadata?.provider || "email";

      const { error: profileInsertError } = await supabase
        .from("profiles")
        .upsert({
          id: signedInUser.id,
          email: signedInUser.email,
          username: username,
          role: "user",
          plan: "FREE",
          provider: loginProvider,
          is_blocked: false,
        });

      if (profileInsertError) {
        console.error("profile insert error:", profileInsertError);
        return { error: profileInsertError };
      }
    }

    return { data };
  };

  const signup = async (email, password, username) => {
    const trimmedUsername = username.trim();

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { username: trimmedUsername },
      },
    });

    console.log("signUp result:", data, error);

    if (error) return { error };

    return { data };
  };

  const loginWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (error) return { error };
    return { data };
  };

  const resetPassword = async (email) => {
    const { data, error } =
      await supabase.auth.resetPasswordForEmail(email);
    if (error) return { error };
    return { data };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    window.location.href = "/login";
  };

  const refreshProfile = async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const session = sessionData?.session;

    if (!session?.user) return;

    const { data: profile } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", session.user.id)
      .single();

    setUser(prev => ({
      ...prev,
      ...(profile || {}),
    }));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithGoogle,
        signup,
        resetPassword,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}